import { useMemo } from "react";
import { Badge, Form } from "react-bootstrap";
import { Button } from "./Controls/Button";
import { MoneyInput } from "./Controls/MoneyInput";
import { ArrowCounterclockwise, XCircle } from "react-bootstrap-icons";

export const LateFeeCalcFlat = 0;
export const LateFeeCalcPercent = 1;

export type PlanLateFeeRuleForm = {
    days: number | "";
    feeCalc: number;
    amount: number | "";
};

export function snapshotLateFeeRules(rules: PlanLateFeeRuleForm[]): string {
    return JSON.stringify(
        [...rules]
            .map((r) => ({
                days: Number(r.days) || 0,
                feeCalc: r.feeCalc,
                amount: Number(r.amount) || 0,
            }))
            .sort((a, b) => a.days - b.days),
    );
}

export function lateFeeRuleDaysInvalid(rule: PlanLateFeeRuleForm) {
    return rule.days === "" || Number(rule.days) <= 0;
}

export function lateFeeRuleAmountInvalid(rule: PlanLateFeeRuleForm) {
    return rule.amount === "" || Number(rule.amount) <= 0;
}

export function lateFeeRulesAreValid(rules: PlanLateFeeRuleForm[]) {
    const days = rules.map((r) => Number(r.days)).filter((d) => d > 0);
    const unique = new Set(days).size === days.length;
    return unique && rules.every((r) => !lateFeeRuleDaysInvalid(r) && !lateFeeRuleAmountInvalid(r));
}

export function toApiLateFeeRules(rules: PlanLateFeeRuleForm[]) {
    return rules.map((r) => ({
        days: Number(r.days),
        feeCalc: r.feeCalc,
        amount: r.feeCalc === LateFeeCalcPercent
            ? Number(r.amount) / 100
            : Math.round(Number(r.amount)),
    }));
}

export function fromApiLateFeeRules(
    rules: { days?: number; feeCalc?: number; amount?: number }[] | null | undefined,
): PlanLateFeeRuleForm[] {
    if (!rules?.length) return [];
    return [...rules]
        .sort((a, b) => (a.days ?? 0) - (b.days ?? 0))
        .map((r) => ({
            days: r.days ?? "",
            feeCalc: r.feeCalc ?? LateFeeCalcFlat,
            amount: r.feeCalc === LateFeeCalcPercent
                ? Math.round((r.amount ?? 0) * 10000) / 100
                : (r.amount ?? 0),
        }));
}

export interface PlanLateFeeRulesSectionProps {
    conversionEnabled: boolean;
    conversionDays: number;
    rules: PlanLateFeeRuleForm[];
    onChange: (rules: PlanLateFeeRuleForm[]) => void;
    disabled?: boolean;
    embedded?: boolean;
    showErrors?: boolean;
}

export function PlanLateFeeRulesSection({
    conversionEnabled,
    conversionDays,
    rules,
    onChange,
    disabled,
    embedded,
    showErrors,
}: PlanLateFeeRulesSectionProps) {
    const addRule = () => {
        onChange([...rules, { days: "", feeCalc: LateFeeCalcFlat, amount: "" }]);
    };

    const updateRule = (index: number, patch: Partial<PlanLateFeeRuleForm>) => {
        onChange(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)));
    };

    const removeRule = (index: number) => {
        onChange(rules.filter((_, i) => i !== index));
    };

    const duplicateDays = useMemo(() => {
        const counts = new Map<number, number>();
        for (const r of rules) {
            const d = Number(r.days);
            if (!d) continue;
            counts.set(d, (counts.get(d) ?? 0) + 1);
        }
        return new Set([...counts.entries()].filter(([, n]) => n > 1).map(([d]) => d));
    }, [rules]);

    const wrapClass = embedded ? "mt-3 pt-3 border-top" : "border rounded p-3 mb-3";

    if (!conversionEnabled) {
        return (
            <div className={wrapClass}>
                <div className="fw-semibold mb-1">Late fees</div>
                <p className="text-secondary small mb-0">
                    Failed autopay does not generate an invoice. Enable “Convert to invoiced parker after failed payments”
                    first so late-fee tiers can fire against an invoice due date.
                </p>
            </div>
        );
    }

    return (
        <div className={wrapClass}>
            <div className="fw-semibold mb-1">Late fees</div>
            <p className="plan-accent-note mb-3">
                <b>Days past due</b> count from each invoice’s due date, not from the failed charge.
                Failed autopay is converted and invoiced <b>{conversionDays} day(s)</b> after the billing
                period starts; that first invoice is due on the period start, so it is already{" "}
                <b>{conversionDays} day(s)</b> overdue when sent. Tiers at or below that apply immediately
                on the conversion invoice. Later monthly invoices use the same due-date clock.
            </p>

            {rules.length === 0 && (
                <div className="d-flex flex-column align-items-center justify-content-center text-center py-4 border rounded mb-3">
                    <p className="text-secondary mb-3 small">No late-fee tiers yet</p>
                    <Button size="sm" variant="outline-primary" type="button" onClick={addRule} disabled={disabled}>
                        + Add Tier
                    </Button>
                </div>
            )}

            {rules.map((rule, i) => (
                <div
                    key={i}
                    className="rounded p-3 mb-3"
                    style={{ border: "1.5px solid rgba(214,51,132,0.3)", background: "rgba(214,51,132,0.02)" }}
                >
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <Badge
                            bg=""
                            style={{
                                border: "1.5px solid #d63384",
                                color: "#d63384",
                                background: "transparent",
                                fontWeight: 500,
                                fontSize: "0.75rem",
                            }}
                        >
                            Tier {i + 1}
                        </Badge>
                        <button
                            type="button"
                            className="btn btn-sm btn-link text-danger text-decoration-none p-0 d-flex align-items-center gap-1"
                            onClick={() => removeRule(i)}
                            disabled={disabled}
                        >
                            <XCircle size={14} />
                            Remove
                        </button>
                    </div>

                    <div className="d-flex align-items-end gap-2 flex-wrap">
                        <div style={{ width: 130 }}>
                            <Form.Label className="small mb-1">Days past due</Form.Label>
                            <Form.Control
                                type="number"
                                min={1}
                                value={rule.days}
                                isInvalid={
                                    (showErrors && lateFeeRuleDaysInvalid(rule))
                                    || (rule.days !== "" && (Number(rule.days) <= 0 || duplicateDays.has(Number(rule.days))))
                                }
                                onChange={(e) =>
                                    updateRule(i, { days: e.target.value === "" ? "" : Number(e.target.value) })
                                }
                                disabled={disabled}
                            />
                            {showErrors && lateFeeRuleDaysInvalid(rule) && (
                                <Form.Text className="d-block text-danger">Required</Form.Text>
                            )}
                            {rule.days !== "" && duplicateDays.has(Number(rule.days)) && (
                                <Form.Text className="d-block text-danger">Days must be unique</Form.Text>
                            )}
                        </div>
                        <div style={{ width: 130 }}>
                            <Form.Label className="small mb-1">Type</Form.Label>
                            <Form.Select
                                value={rule.feeCalc}
                                onChange={(e) =>
                                    updateRule(i, { feeCalc: Number(e.target.value), amount: "" })
                                }
                                disabled={disabled}
                            >
                                <option value={LateFeeCalcFlat}>Flat</option>
                                <option value={LateFeeCalcPercent}>Percent</option>
                            </Form.Select>
                        </div>
                        {rule.feeCalc === LateFeeCalcPercent ? (
                            <div style={{ width: 140 }}>
                                <Form.Label className="small mb-1">Percent</Form.Label>
                                <Form.Control
                                    type="number"
                                    min={0.01}
                                    step={0.01}
                                    max={100}
                                    value={rule.amount}
                                    isInvalid={showErrors && lateFeeRuleAmountInvalid(rule)}
                                    onChange={(e) =>
                                        updateRule(i, { amount: e.target.value === "" ? "" : Number(e.target.value) })
                                    }
                                    disabled={disabled}
                                />
                                {showErrors && lateFeeRuleAmountInvalid(rule) && (
                                    <Form.Text className="d-block text-danger">Must be greater than 0</Form.Text>
                                )}
                            </div>
                        ) : (
                            <div style={{ minWidth: 160 }}>
                                <MoneyInput
                                    name={`lateFeeAmount-${i}`}
                                    label="Amount"
                                    labelClassName="small mb-1"
                                    className="mb-0"
                                    inputClassName={`text-end${showErrors && lateFeeRuleAmountInvalid(rule) ? " is-invalid" : ""}`}
                                    value={rule.amount === "" ? 0 : Number(rule.amount)}
                                    onChange={async (e) => {
                                        const n = Number(e.target.value);
                                        updateRule(i, { amount: Number.isNaN(n) ? "" : n });
                                    }}
                                    onBlur={async () => undefined}
                                    error={showErrors && lateFeeRuleAmountInvalid(rule)
                                        ? { type: "validate", message: "Must be greater than $0.00" }
                                        : undefined}
                                />
                            </div>
                        )}
                        <div>
                            <Form.Label className="small mb-1 d-block">&nbsp;</Form.Label>
                            <button
                                type="button"
                                className="btn btn-sm btn-link text-secondary text-decoration-none px-1 d-flex align-items-center gap-1"
                                onClick={() => updateRule(i, { amount: 0 })}
                                disabled={disabled}
                                title="Reset to 0"
                            >
                                <ArrowCounterclockwise size={14} />
                                Reset
                            </button>
                        </div>
                    </div>
                    {rule.days !== "" && Number(rule.days) > 0 && Number(rule.days) <= conversionDays && (
                        <p className="plan-accent-note mb-0 mt-3">
                            Conversion invoice is already {conversionDays} day(s) past due when issued,
                            so this tier applies as soon as that invoice exists. Later monthly invoices
                            wait the full <b>{rule.days} day(s)</b> after their due date.
                        </p>
                    )}
                </div>
            ))}

            {rules.length > 0 && (
                <button
                    type="button"
                    className="btn w-100 fw-semibold"
                    style={{
                        border: "2px dashed #d63384",
                        borderRadius: 8,
                        color: "#d63384",
                        background: "transparent",
                    }}
                    onClick={addRule}
                    disabled={disabled}
                >
                    + Add another tier
                </button>
            )}
        </div>
    );
}
